import { z } from 'zod';
/**
 * Onboarding reveal — the wire shape for
 * `GET /api/orgs/:o/onboarding/reveal`.
 *
 * The `/onboarding/reveal` surface is the post-onboarding "magic
 * moment" page that sits between the wizard's completion and the
 * user's first `/today` view. It surfaces a synthesized read of:
 *   - the top-12 people the operator cares about (Slack/email/calendar)
 *   - the rituals they run on (recurring meetings + Slack rhythm)
 *   - the metrics they check most (BC.northStarMetric + scorecard)
 *   - the vocabulary the agent will use (industry-keyed translation)
 *
 * Categories with no data return as empty arrays — the panel renders
 * an honest empty-state. The aggregate confidences are panel-level
 * meters that the designer reference (`assets/agentic.jsx` §11) shows
 * as a small `% conf` pill in each panel's header.
 *
 * The synthesizer lives at
 * `apps/functions/src/synthesis/onboarding-reveal-synth.ts`. It logs a
 * `synthesized` AgentAction because it's a heavy compute the CEO
 * should be able to audit (this is a one-time burst per org).
 */
/** Cadence label for a detected ritual. Closed enum — surfaces in the panel row. */
export const RevealRitualCadence = z.enum([
    'daily',
    'weekly',
    'bi-weekly',
    'monthly',
    'quarterly',
    'ad-hoc',
]);
/** Trend direction for a reveal metric row — drives the colored dot. */
export const RevealMetricTrend = z.enum(['up', 'flat', 'down']);
/** One person row in the people panel. */
export const RevealPerson = z.object({
    /** Firebase uid when the person is an OrgMember; absent for inferred-only people. */
    uid: z.string().optional(),
    /** Stable identifier (uid, email, or canonical key). */
    key: z.string().min(1),
    /** Display label. */
    label: z.string().min(1).max(120),
    /** Optional role string ("Head of Eng", "VP Sales"). */
    role: z.string().max(120).optional(),
    /** 0..1 confidence in the resolution + relevance. */
    confidence: z.number().min(0).max(1),
});
/** One ritual row in the rituals panel. */
export const RevealRitual = z.object({
    key: z.string().min(1),
    /** Short title — "Weekly L10", "Board sync", "1:1 with Jenna". */
    label: z.string().min(1).max(120),
    /**
     * Human-readable cadence string — "every Friday 9am", "monthly first Wed".
     * Free-form so the synthesizer can carry calendar-RRULE summaries verbatim.
     */
    cadence: z.string().min(1).max(80),
    /** Detected normalized cadence (for sorting / filtering). */
    cadenceKind: RevealRitualCadence.optional(),
    /** Count of recurring attendees (when known from calendar ingest). */
    participantCount: z.number().int().min(0).optional(),
    confidence: z.number().min(0).max(1),
});
/** One metric row in the metrics panel. */
export const RevealMetric = z.object({
    key: z.string().min(1),
    /** Human label — "Net new ARR", "Pipeline coverage". */
    label: z.string().min(1).max(120),
    /** Formatted display value — "$48k", "1.4×", "—" when no live value. */
    currentValue: z.string().max(40).optional(),
    trend: RevealMetricTrend.optional(),
    confidence: z.number().min(0).max(1),
});
/** One vocabulary pair — "customers → clients" style translation. */
export const RevealVocabularyPair = z.object({
    /** The generic-industry term the agent would default to. */
    industryTerm: z.string().min(1).max(40),
    /** The operator's preferred term. */
    customerTerm: z.string().min(1).max(40),
});
/** Read-stats subobject — drives the "messages / docs / records" sub-copy. */
export const RevealReadStats = z.object({
    messagesRead: z.number().int().min(0),
    docsRead: z.number().int().min(0),
    recordsRead: z.number().int().min(0),
});
/** Wire shape returned by `GET /api/orgs/:o/onboarding/reveal`. */
export const OnboardingRevealResponse = z.object({
    people: z.array(RevealPerson).max(12),
    /** 0..1 aggregate confidence for the people panel. */
    peopleConfidence: z.number().min(0).max(1),
    rituals: z.array(RevealRitual).max(8),
    ritualsConfidence: z.number().min(0).max(1),
    metrics: z.array(RevealMetric).max(6),
    metricsConfidence: z.number().min(0).max(1),
    vocabulary: z.array(RevealVocabularyPair).max(10),
    /** Total wall-clock duration of the read in milliseconds. */
    readDurationMs: z.number().int().min(0),
    readStats: RevealReadStats,
});
